import { valibotResolver } from "@hookform/resolvers/valibot";
import { useForm } from "react-hook-form";

import MaxLengthInput from "common/components/MaxLengthInput";
import { Button } from "common/components/ui/Button";
import {
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { getSchemaMaxLength } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import { CampaignNameSchema } from "common/schemas";

import useCampaignMutations from "../../datahooks/useCampaignMutations";

interface CampaignNameEditProps {
  campaignName: string;
  campaignId: string;
  onClose: () => void;
}

export default function CampaignNameEdit({
  campaignName,
  campaignId,
  onClose,
}: CampaignNameEditProps) {
  const isDesktop = useTwBreakpoint("lg");
  const Footer = isDesktop ? DialogFooter : DrawerFooter;

  const { updateCampaign, isUpdatingCampaign } = useCampaignMutations();

  const {
    reset,
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: { name: campaignName },
    resolver: valibotResolver(CampaignNameSchema),
  });

  function updateCampaignName({ name }: { name: string }) {
    updateCampaign({
      updates: { name },
      campaignId,
    }).then(() => {
      reset({ name });
      onClose();
    });
  }

  function onSubmit() {
    handleSubmit(updateCampaignName)();
  }

  const Body = isDesktop ? DialogBody : DrawerBody;

  const Header = isDesktop ? DialogHeader : DrawerHeader;

  return (
    <>
      <Header title="Change campaign name" />

      <Body>
        <FormField
          label="Campaign name"
          htmlFor="name-input"
          size="lg"
          className="mb-8"
          error={errors?.name?.message}
        >
          <MaxLengthInput
            variant="lg"
            id="name-input"
            characterLength={watch("name").length}
            maxLength={getSchemaMaxLength(CampaignNameSchema.entries.name)}
            {...register("name")}
          />
        </FormField>
      </Body>

      <Footer className="justify-end">
        <Button
          variant="secondary-black"
          disabled={isUpdatingCampaign}
          onClick={onClose}
        >
          Cancel
        </Button>

        <Button isLoading={isUpdatingCampaign} onClick={onSubmit}>
          Save
        </Button>
      </Footer>
    </>
  );
}
